import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Icon, Input, Pagination, Radio, Spin } from "antd";
import axios from "axios";
import CommonHeader from "../../CommonHeader";
import CustomHeader from "../../../../CommonHeader/CustomHeader";
import CustomAgGridTable from "../../../../Common-Table/CustomAgGridTable";
import {
  EA,
  FOLLOW_UP_SPECIALIST,
  FOLLOWUP,
  LEAD,
  OPPORTUNITY,
  PROSPECT,
  REVIEW,
  REVIEW_SCHEDULER,
  REVIEWADMIN,
  TYPE_TO_FILTER_BY_NAME,
} from "../../../../utils/Constants";
import {
  GET_FS_ALL_AGENCIES,
  RS_ALL_AGENCIES_API_URL,
  WEEKLY_DASH_STAT_GRID,
} from "../../../../utils/Api_enums";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import "../../../../Common-Table/CustomAgGridTable.css";
import "./styles/style.css";
import moment from "moment";
import F from "../../../../Functions";
import { useBoundStore } from "../../../../store/useBoundStore";

const PAGE_SIZE = 10;

const AGENT_STAT_KEYS = [
  "appointment_goal",
  "agents_under",
  "agents_ten_plus",
  "agents_zero",
  "assigned_agents",
  "unassigned_agents",
];

const STATS_WITH_CALLS_MADE = [
  "appointment_goal",
  "agents_under",
  "agents_zero",
];

/** UI field → API sort key */
const SORT_KEY_MAP = {
  agency: "agency",
  agency_name: "agency",
  scheduler: "scheduler",
  scheduler_name: "scheduler",
  active_month: "active_month",
  activeMonth: "active_month",
  list: "list",
  reviewList: "list",
  calls_made: "calls_made",
  callsMade: "calls_made",
  appointments_scheduled: "appointments_scheduled",
  appointmentsScheduled: "appointments_scheduled",
  appointment_scheduled_percentage: "appointment_scheduled_percentage",
  appointmentScheduledPercentage: "appointment_scheduled_percentage",
  total_calls: "total_calls",
  totalCalls: "total_calls",
  callback_requested: "callback_requested",
  callbackRequested: "callback_requested",
  requested_reschedule: "requested_reschedule",
  requestedReschedule: "requested_reschedule",
};

const toApiCarrier = (carrier) =>
  !carrier || carrier === "All Carrier" ? "All Carriers" : carrier;

const getListCount = (item = {}) => {
  if (item.review_list != null) return item.review_list;
  if (item.lead_list != null) return item.lead_list;
  if (item.opportunity_list != null) return item.opportunity_list;
  return 0;
};

const readStatCard = () => {
  try {
    return JSON.parse(localStorage.getItem("weeklyDashboardStatCard")) || {};
  } catch (err) {
    return {};
  }
};

const formatAppointmentScheduledPercent = (value) => {
  if (value == null || value === "") return "0%";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return `${value}%`;
  return `${numeric}%`;
};

const mapListRows = (responseData = []) =>
  responseData.map((item, index) => {
    const schedulerRole = item.scheduler_role ? ` (${item.scheduler_role})` : "";
    const schedulerName = item.scheduler_name || "—";
    const appointmentPct =
      item.total_appointments_scheduled_percentage != null
        ? item.total_appointments_scheduled_percentage
        : item.appointment_scheduled_percentage;
    return {
      id: `${item.agency_id || "agency"}-${item.user_id || index}`,
      agency: item.agency_name || "—",
      agency_name: item.agency_name || "—",
      agency_id: item.agency_id,
      scheduler: `${schedulerName}`,
      scheduler_name: schedulerName,
      scheduler_role: item.scheduler_role || "",
      scheduler_id: item.user_id,
      activeMonth: item.active_month || "—",
      active_month: item.active_month || "—",
      reviewList: String(getListCount(item)),
      callsMade: String(item.calls_made != null ? item.calls_made : 0),
      appointmentsScheduled: String(
        item.appointments_scheduled != null ? item.appointments_scheduled : 0
      ),
      appointmentScheduledPercentage: formatAppointmentScheduledPercent(
        appointmentPct
      ),
      totalCalls: String(item.total_calls != null ? item.total_calls : 0),
      callbackRequested: String(
        item.callback_requested != null ? item.callback_requested : 0
      ),
      requestedReschedule: String(
        item.requested_reschedule != null ? item.requested_reschedule : 0
      ),
      type_of_call: item.type_of_call,
      raw: item,
    };
  });

const WeeklyDashboardStatList = (props) => {
  const locationState =
    (props.location && props.location.state) || readStatCard() || {};

  const { RSFSCardFilters } = useBoundStore();
  const [role, setRole] = useState(
    locationState.role || RSFSCardFilters.selectedAdminToggle || REVIEWADMIN
  );
  const [toggleAdminSwitchRole, setAdminToggleSwitchRole] = useState(
    locationState.toggleAdminSwitchRole || LEAD
  );
  const [selectedMonth] = useState(
    locationState.selectedMonth || moment().format("M")
  );
  const [reportDateRange] = useState(() => {
    if (locationState.start_date && locationState.end_date) {
      return {
        start_date: locationState.start_date,
        end_date: locationState.end_date,
      };
    }
    // Support older saved drill-down state that only contains a month.
    const month = moment().startOf("month").month(Number(selectedMonth) - 1);
    return {
      start_date: month.clone().startOf("month").format("YYYY-MM-DD"),
      end_date: month.clone().endOf("month").format("YYYY-MM-DD"),
    };
  });
  const [selectedCarrier] = useState(
    locationState.selectedCarrier || "All Carrier"
  );
  const [statKey] = useState(locationState.statKey || "");
  const [statTitle] = useState(locationState.statTitle || "Stat List");
  const isUnassignedAgents = statKey === "unassigned_agents";
  const selectedSchedulerId =
    locationState.schedulerId != null
      ? String(locationState.schedulerId)
      : /^\d+$/.test(String(locationState.selectedScheduler || ""))
        ? String(locationState.selectedScheduler)
        : undefined;

  const [boardRows, setBoardRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState(locationState.searchText || "");
  const [selectedAgency, setSelectedAgency] = useState(
    locationState.selectedAgency
  );
  const [agencies, setAgencies] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [sortField, setSortField] = useState("agency");
  const [sortDirection, setSortDirection] = useState("asc");
  const [sortOrders, setSortOrders] = useState({ agency: "asc" });

  const resetHandlers = useRef([]);
  const filterTimeout = useRef(null);

  const monthOptions = Array.isArray(F.getMonths()) ? F.getMonths() : [];
  const selectedMonthLabel = useMemo(() => {
    const month = monthOptions.find((m) => String(m.id) === String(selectedMonth));
    return month
      ? month.name
      : moment()
        .month(Number(selectedMonth) - 1)
        .format("MMMM");
  }, [monthOptions, selectedMonth]);

  const typeOfCall =
    role === EA
      ? toggleAdminSwitchRole === LEAD
        ? PROSPECT
        : FOLLOWUP
      : REVIEW;
  const schedulerHeader = role === EA ? FOLLOW_UP_SPECIALIST : REVIEW_SCHEDULER;
  const activeMonthHeader =
    // !isUnassignedAgents &&
    role === EA ? "Follow-up Active Month" : "Review Active Month";
  const listHeader =
    // !isUnassignedAgents &&
     role === EA
      ? toggleAdminSwitchRole === LEAD
        ? "Lead List"
        : "Opportunity List"
      : "Review List";


  const fetchAllRSAgencies = async () => {
    try {
      const response = await axios.get(RS_ALL_AGENCIES_API_URL);
      setAgencies(response.data || []);
    } catch (err) {
      console.error("Error fetching RS agencies:", err);
    }
  };

  const fetchAllFSAgencies = async () => {
    try {
      const response = await axios.get(GET_FS_ALL_AGENCIES);
      setAgencies(response.data || []);
    } catch (err) {
      console.error("Error fetching FS agencies:", err);
    }
  };

  useEffect(() => {
    if (role === REVIEWADMIN) {
      fetchAllRSAgencies();
    } else {
      fetchAllFSAgencies();
    }
  }, [role]);

  const fetchBoardRows = async () => {
    if (!selectedMonth || !selectedCarrier || !statKey) return;
    if (!AGENT_STAT_KEYS.includes(statKey)) return;

    try {
      setLoading(true);
      const apiSortKey = SORT_KEY_MAP[sortField] || sortField || "agency";
      const queryParams = new URLSearchParams({
        stat: isUnassignedAgents ? "non_assigned_agents" : statKey,
        month: Number(selectedMonth),
        ...reportDateRange,
        type_of_call: typeOfCall,
        carrier: toApiCarrier(selectedCarrier),
        page: currentPage,
        per_page: PAGE_SIZE,
        sort: `${apiSortKey}:${sortDirection || "asc"}`,
      });

      const searchTerm = (searchText || "").trim();
      if (searchTerm) {
        queryParams.append("search", searchTerm);
      }
      if (selectedAgency) {
        queryParams.append("agency_id", selectedAgency);
      }
      if (selectedSchedulerId != null && selectedSchedulerId !== "") {
        queryParams.append("scheduler_id", selectedSchedulerId);
      }

      const response = await axios.get(
        `${WEEKLY_DASH_STAT_GRID}?${queryParams.toString()}`
      );
      const payload = response.data || {};
      setBoardRows(mapListRows(payload.data || []));
      setTotalPages(payload.total_pages || 0);
    } catch (error) {
      console.error("Error fetching weekly dashboard stat list:", error);
      setBoardRows([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(
      () => {
        fetchBoardRows();
      },
      searchText ? 400 : 0
    );
    return () => clearTimeout(delay);
  }, [
    reportDateRange,
    selectedMonth,
    selectedCarrier,
    typeOfCall,
    currentPage,
    sortField,
    sortDirection,
    searchText,
    selectedAgency,
    selectedSchedulerId,
    statKey,
    role,
  ]);

  const handleSort = (field) => {
    const apiKey = SORT_KEY_MAP[field] || field;
    setSortField((prevField) => {
      const prevApiKey = SORT_KEY_MAP[prevField] || prevField;
      const newDirection =
        prevApiKey === apiKey && sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(newDirection);
      setSortOrders({ [apiKey]: newDirection });
      return apiKey;
    });
    setCurrentPage(1);
  };

  const handleFilter = (field, value) => {
    setCurrentPage(1);

    if (field === "agency_name" || field === "agency") {
      setSelectedAgency(value || undefined);
      return;
    }

    if (field === "scheduler_name" || field === "scheduler") {
      clearTimeout(filterTimeout.current);
      filterTimeout.current = setTimeout(() => {
        setSearchText(value || "");
      }, 300);
    }
  };

  const handleFilterResetHandler = (resetFn) => {
    resetHandlers.current.push(resetFn);
  };

  const handleBackToDashboard = () => {
    localStorage.removeItem("weeklyDashboardStatCard");
    props.history.push({
      pathname: "/dashboard/monthly-report",
      state: {
        fromStatList: true,
        selectedDateRange: locationState.selectedDateRange !== undefined
          ? locationState.selectedDateRange : null,
        ...reportDateRange,
        role,
        toggleAdminSwitchRole,
        selectedMonth,
        selectedCarrier,
        selectedAgency: locationState.selectedAgency,
        searchText: locationState.selectedScheduler
          ? ""
          : locationState.searchText || "",
        selectedScheduler: locationState.selectedScheduler,
        schedulerId: selectedSchedulerId,
      },
    });
  };

  const handleRoleChange = (newRole) => {
    setRole(newRole);
    setAdminToggleSwitchRole(LEAD);
    setCurrentPage(1);
  };

  const handleRoleSwitchChange = (fsRole) => {
    setAdminToggleSwitchRole(fsRole);
    setCurrentPage(1);
  };

  const refresh = () => {
    resetHandlers.current.forEach((reset) => reset());
    resetHandlers.current = [];

    setSearchText("");
    setSelectedAgency(undefined);
    setSortField("agency");
    setSortDirection("asc");
    setSortOrders({ agency: "asc" });
    setCurrentPage(1);
  };

  const columnDefs = useMemo(
    () => [
      {
        headerClass: "custom-ag-header",
        headerComponent: "CustomHeader",
        headerComponentParams: (params) => ({
          headers: ["Agency"],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: "agency",
          sortOrder: sortOrders.agency,
          handleSort,
          handleFilter,
          showPopOver: true,
          inputType: "select",
          options: agencies,
          handleFilterResetHandler,
        }),
        field: "agency",
        minWidth: 250,
        sortable: true,
        filter: true,
      },
      {
        headerName: schedulerHeader,
        headerClass: "custom-ag-header",
        headerComponent: "CustomHeader",
        headerComponentParams: (params) => ({
          headers: [schedulerHeader],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: "scheduler",
          sortOrder: sortOrders.scheduler,
          handleSort,
          handleFilter,
          placeholder: TYPE_TO_FILTER_BY_NAME,
          showPopOver: true,
          inputType: "text",
          handleFilterResetHandler,
        }),
        field: "scheduler",
        minWidth: 200,
        sortable: true,
        filter: true,
      },
      {
        headerClass: "custom-ag-header",
        headerComponent: "CustomHeader",
        headerComponentParams: (params) => ({
          headers: [activeMonthHeader],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: "active_month",
          sortOrder: sortOrders.active_month,
          handleSort,
          showPopOver: false,
        }),
        field: "activeMonth",
        minWidth: 150,
        sortable: true,
        filter: false,
      },
      {
        headerName: listHeader,
        headerClass: "custom-ag-header",
        headerComponent: "CustomHeader",
        headerComponentParams: (params) => ({
          headers: [listHeader],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: "list",
          sortOrder: sortOrders.list,
          handleSort,
          showPopOver: false,
        }),
        field: "reviewList",
        minWidth: 120,
        sortable: true,
        filter: false,
      },
      // ...(STATS_WITH_CALLS_MADE.includes(statKey)
      //   ? [
      //     {
      //       headerClass: "custom-ag-header",
      //       headerComponent: "CustomHeader",
      //       headerComponentParams: (params) => ({
      //         headers: ["Calls Made"],
      //         colDef: params.column.getColDef(),
      //         columnApi: params.columnApi,
      //         field: "calls_made",
      //         sortOrder: sortOrders.calls_made,
      //         handleSort,
      //         showPopOver: false,
      //       }),
      //       field: "callsMade",
      //       minWidth: 120,
      //       sortable: true,
      //       filter: false,
      //     },
      //   ]
      //   : []),
      {
        headerClass: "custom-ag-header",
        headerComponent: "CustomHeader",
        headerComponentParams: (params) => ({
          headers: ["Appointments Scheduled"],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: "appointments_scheduled",
          sortOrder: sortOrders.appointments_scheduled,
          handleSort,
          showPopOver: false,
        }),
        field: "appointmentsScheduled",
        minWidth: 180,
        sortable: true,
        filter: false,
      },
      {
        headerClass: "custom-ag-header",
        headerComponent: "CustomHeader",
        headerComponentParams: (params) => ({
          headers: ["Appointment Scheduled %"],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: "appointment_scheduled_percentage",
          sortOrder: sortOrders.appointment_scheduled_percentage,
          handleSort,
          showPopOver: false,
        }),
        field: "appointmentScheduledPercentage",
        minWidth: 190,
        sortable: true,
        filter: false,
      },
    ].filter((column) =>
      !isUnassignedAgents ||
      ["agency", "activeMonth", "reviewList"].includes(column.field)
    ),
    [sortOrders, agencies, schedulerHeader, activeMonthHeader, listHeader, statKey, isUnassignedAgents]
  );

  return (
    <div className="monthly-dashboard-page">
      <div className="monthly-dashboard-title">{statTitle}</div>
      <div
        role="button"
        tabIndex="0"
        className="monthly-dashboard-back hyperlink-shomore-stat"
        onClick={handleBackToDashboard}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleBackToDashboard();
          }
        }}
      >
        <Icon type="arrow-left" />
        Back to Dashboard
      </div>

      <CommonHeader
        user={(props.auth && props.auth.user) || props.user || {}}
        onRoleChange={handleRoleChange}
        isHidden={false}
      />

      {role === EA && (
        <div className="monthly-dashboard-actions" style={{ marginTop: 16 }}>
          <Radio.Group
            value={toggleAdminSwitchRole}
            onChange={(e) => handleRoleSwitchChange(e.target.value)}
            className="monthly-dashboard-lead-toggle"
          >
            <Radio.Button
              value={LEAD}
              className={
                toggleAdminSwitchRole === LEAD
                  ? "review-btn-active"
                  : "review-btn-inactive"
              }
              style={{
                width: 120,
                textAlign: "center",
                borderRadius: "20px 0px 0px 20px",
              }}
            >
              {LEAD}
            </Radio.Button>
            <Radio.Button
              value={OPPORTUNITY}
              className={
                toggleAdminSwitchRole === OPPORTUNITY
                  ? "ea-btn-active"
                  : "ea-btn-inactive"
              }
              style={{
                width: 120,
                textAlign: "center",
                borderRadius: "0px 20px 20px 0px",
              }}
            >
              {OPPORTUNITY}
            </Radio.Button>
          </Radio.Group>
        </div>
      )}

      <section className="monthly-dashboard-board" style={{ marginTop: 24 }}>
        <div className="monthly-dashboard-board-title">
          <h3>
            <Icon type="table" />
            {statTitle}
          </h3>
          <div className="monthly-dashboard-board-title-actions">
            <Input
              className="monthly-dashboard-board-search"
              placeholder="Search"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setCurrentPage(1);
              }}
              suffix={<Icon type="search" />}
            />
            <Button
              type="primary"
              className="monthly-dashboard-refresh"
              loading={loading}
              onClick={refresh}
            >
              <Icon type="reload" />
            </Button>
            <span>
              {selectedMonthLabel} · {selectedCarrier}
            </span>
          </div>
        </div>

        <div className="monthly-dashboard-aggrid" style={{ position: "relative" }}>
          <CustomAgGridTable
            rowDatas={boardRows}
            columnDefss={columnDefs}
            gridApiOptions={{
              defaultColDef: {
                sortable: true,
                filter: true,
                resizable: true,
                flex: 1,
              },
            }}
            frameworkComponents={{
              CustomHeader,
            }}
          />
          {loading && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(255,255,255,0.6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
              }}
            >
              <Spin tip="Loading ..." />
            </div>
          )}
        </div>

        <div className="monthly-dashboard-pagination">
          <Pagination
            current={currentPage}
            total={totalPages * PAGE_SIZE}
            pageSize={PAGE_SIZE}
            showSizeChanger={false}
            onChange={(page) => setCurrentPage(page)}
          />
        </div>
      </section>
    </div>
  );
};

export default WeeklyDashboardStatList;



// WEBPACK FOOTER //
// ./src/scenes/Dashboard/parts/review-admin/WeeklyDashboardStatList.js